import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationBell from '@/components/NotificationBell';
import MobileBottomNav from '@/components/MobileBottomNav';
import {
  LayoutDashboard, FileCheck, ClipboardCheck, Building2, FileText, Users,
  Settings, LogOut, ChevronLeft, ChevronRight, BarChart3, ListTodo, Inbox,
  Award, Timer, Globe, Menu, X, SquareKanban, BookOpen, Shield, User,
  CreditCard, FileEdit, Wallet, MessageCircle, Activity, UserPlus, Crown,
  Sparkles, MessageSquarePlus, Receipt, Mail, Beaker, Briefcase, Newspaper,
  Search, Command, ArrowUpRight, ChevronDown, ChevronUp, TrendingUp,
} from 'lucide-react';
import usePlan from '@/hooks/usePlan';
import { PlanCard } from '@/components/PlanBadge';

const ease = [0.22, 1, 0.36, 1];
const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '');

/* ════════════════════════════════════════════════════════════════════════
   ZERTIVO DASHBOARD LAYOUT — Premium SaaS Shell (Iteration 153)
   ────────────────────────────────────────────────────────────────────────
   Single leverage-point that wraps every admin/portal/auditor page.
   Redesigning this shell upgrades 70+ pages to premium SaaS quality.

   Design DNA: Linear × Notion × Stripe Dashboard
   - Off-white canvas (#FAFAFA) with subtle dotted grid for depth
   - Sidebar: filterable, collapsible sections, animated active-pill
     with layoutId, sticky search at top
   - Topbar: breadcrumb left · ⌘K search trigger · notifications · user
   - Glass-morphic surfaces, accent green pulses, spring animations
   ════════════════════════════════════════════════════════════════════════ */

/* ─── Section persistence helpers ─────────────────────────────────────── */
const SEC_KEY = 'zv:dash:sec-collapsed:v1';
const readCollapsedSections = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEC_KEY) || '[]')); }
  catch { return new Set(); }
};
const writeCollapsedSections = (set) => {
  try { localStorage.setItem(SEC_KEY, JSON.stringify([...set])); } catch {}
};

/* ─── Compact language picker ─────────────────────────────────────────── */
function LangPicker({ lang, setLang, locales }) {
  const [open, setOpen] = useState(false);
  const current = locales.find(l => l.code === lang) || locales[0];
  if (!current) return null;
  if (locales.length <= 2) {
    const other = locales.find(l => l.code !== lang) || locales[0];
    return (
      <button
        onClick={() => setLang(other.code)}
        data-testid="lang-toggle"
        className="hidden sm:flex items-center gap-1.5 h-8 px-2.5 rounded-full hover:bg-[#F4F4F5] transition-colors text-[11.5px] font-medium text-[#52525B]"
        title={`Switch to ${other.name}`}
      >
        <span className="text-[12px] leading-none">{other.flag}</span>
        <span className="uppercase tracking-wider">{other.code}</span>
      </button>
    );
  }
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="lang-toggle"
        className="hidden sm:flex items-center gap-1.5 h-8 px-2.5 rounded-full hover:bg-[#F4F4F5] transition-colors text-[11.5px] font-medium text-[#52525B]"
      >
        <span className="text-[12px] leading-none">{current.flag}</span>
        <span className="uppercase tracking-wider">{current.code}</span>
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-10 z-40 w-44 rounded-xl bg-white border border-[#E4E4E7] shadow-[0_14px_30px_-10px_rgba(0,0,0,0.15)] overflow-hidden"
            >
              {locales.map(l => (
                <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
                  data-testid={`lang-opt-${l.code}`}
                  className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[12.5px] transition-colors ${
                    l.code === lang ? 'bg-[#E6FBF3] text-[#0F8A5F] font-semibold' : 'hover:bg-[#FAFAFA] text-[#09090B]'
                  }`}>
                  <span className="text-[14px] leading-none">{l.flag}</span>
                  <span className="flex-1 text-left">{l.name}</span>
                  {l.code === lang && <span className="text-[#00D387]">✓</span>}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Menu items per role ─────────────────────────────────────────────── */
function useMenuItems() {
  const { t } = useLanguage();
  const { isEnterprise } = usePlan();
  return {
    super_admin: [
      { section: 'Überblick', items: [
        { label: t('admin.nav.dashboard') || 'Dashboard', icon: LayoutDashboard, path: '/admin' },
        { label: t('admin.nav.reports') || 'Reports', icon: BarChart3, path: '/admin/reports' },
      ]},
      { section: 'Operativ', items: [
        { label: t('admin.nav.certificates') || 'Zertifikate', icon: FileCheck, path: '/admin/certificates' },
        { label: t('admin.nav.audits') || 'Audits', icon: ClipboardCheck, path: '/admin/audits' },
        { label: 'ÆGIS Konsole', icon: Sparkles, path: '/admin/aegis' },
        { label: t('admin.nav.companies') || 'Unternehmen', icon: Building2, path: '/admin/companies' },
        { label: t('admin.nav.applications') || 'Anträge', icon: Inbox, path: '/admin/applications' },
        { label: t('admin.nav.pipeline') || 'Pipeline', icon: SquareKanban, path: '/admin/pipeline' },
        { label: t('admin.nav.tasks') || 'Aufgaben', icon: ListTodo, path: '/admin/tasks' },
      ]},
      { section: 'Wachstum', items: [
        { label: t('admin.nav.subscriptions') || 'Abonnements', icon: CreditCard, path: '/admin/subscriptions' },
        { label: 'SaaS-Metriken', icon: TrendingUp, path: '/admin/billing-metrics' },
        { label: 'Referrals', icon: Users, path: '/admin/referrals' },
        { label: 'Buchhaltung', icon: Receipt, path: '/admin/accounting' },
        { label: 'Boosts', icon: Sparkles, path: '/admin/boosts' },
        { label: t('nav.blog') || 'Blog', icon: BookOpen, path: '/admin/blog' },
        { label: 'Inhalte', icon: FileEdit, path: '/admin/content' },
        { label: 'A/B-Tests', icon: Beaker, path: '/admin/ab-tests' },
        { label: t('admin.nav.seo') || 'SEO', icon: Globe, path: '/admin/seo' },
      ]},
      { section: 'Team', items: [
        { label: t('admin.nav.users') || 'Benutzer', icon: Users, path: '/admin/users' },
        { label: 'Rollen', icon: Shield, path: '/admin/roles' },
        { label: 'Einladungen', icon: UserPlus, path: '/admin/invites' },
        { label: 'Karriere', icon: Briefcase, path: '/admin/careers' },
        { label: 'Newsroom', icon: Newspaper, path: '/admin/press' },
        { label: 'Support', icon: MessageCircle, path: '/admin/support' },
      ]},
      { section: 'System', items: [
        { label: 'Status-Seite', icon: Activity, path: '/admin/status' },
        { label: t('admin.nav.logs') || 'Logs', icon: FileText, path: '/admin/logs' },
        { label: 'Sicherheit', icon: Shield, path: '/admin/security' },
        { label: t('admin.nav.settings') || 'Einstellungen', icon: Settings, path: '/admin/settings' },
        { label: 'E-Mail DNS', icon: Mail, path: '/admin/email-dns' },
        { label: t('profile.title') || 'Profil', icon: User, path: '/admin/profile' },
      ]},
    ],
    admin: [
      { section: 'Überblick', items: [
        { label: t('admin.nav.dashboard') || 'Dashboard', icon: LayoutDashboard, path: '/admin' },
        { label: t('admin.nav.reports') || 'Reports', icon: BarChart3, path: '/admin/reports' },
      ]},
      { section: 'Operativ', items: [
        { label: t('admin.nav.certificates') || 'Zertifikate', icon: FileCheck, path: '/admin/certificates' },
        { label: t('admin.nav.audits') || 'Audits', icon: ClipboardCheck, path: '/admin/audits' },
        { label: 'ÆGIS Konsole', icon: Sparkles, path: '/admin/aegis' },
        { label: t('admin.nav.companies') || 'Unternehmen', icon: Building2, path: '/admin/companies' },
        { label: t('admin.nav.applications') || 'Anträge', icon: Inbox, path: '/admin/applications' },
        { label: t('admin.nav.pipeline') || 'Pipeline', icon: SquareKanban, path: '/admin/pipeline' },
        { label: t('admin.nav.tasks') || 'Aufgaben', icon: ListTodo, path: '/admin/tasks' },
      ]},
      { section: 'Content', items: [
        { label: t('nav.blog') || 'Blog', icon: BookOpen, path: '/admin/blog' },
        { label: 'Inhalte', icon: FileEdit, path: '/admin/content' },
      ]},
    ],
    auditor: [
      { section: 'Audits', items: [
        { label: t('admin.nav.dashboard') || 'Dashboard', icon: LayoutDashboard, path: '/auditor' },
        { label: t('admin.nav.myAudits') || 'Meine Audits', icon: ClipboardCheck, path: '/auditor/audits' },
        { label: t('admin.nav.tasks') || 'Aufgaben', icon: ListTodo, path: '/auditor/tasks' },
      ]},
    ],
    support: [
      { section: 'Support', items: [
        { label: t('admin.nav.dashboard') || 'Dashboard', icon: LayoutDashboard, path: '/admin' },
        { label: t('admin.nav.applications') || 'Anträge', icon: Inbox, path: '/admin/applications' },
        { label: t('admin.nav.tasks') || 'Aufgaben', icon: ListTodo, path: '/admin/tasks' },
      ]},
    ],
    company: [
      { section: 'Überblick', items: [
        { label: t('admin.nav.dashboard') || 'Dashboard', icon: LayoutDashboard, path: '/portal' },
        { label: 'Mein Unternehmen', icon: Building2, path: '/portal/company' },
        { label: 'Analytics', icon: BarChart3, path: '/portal/analytics' },
      ]},
      { section: 'Vertrauen', items: [
        { label: t('admin.nav.certificates') || 'Zertifikate', icon: FileCheck, path: '/portal/certificates' },
        { label: t('admin.nav.auditStatus') || 'Audits', icon: ClipboardCheck, path: '/portal/audits' },
        { label: 'Badges', icon: Award, path: '/portal/badges' },
        { label: t('admin.nav.documents') || 'Dokumente', icon: FileText, path: '/portal/documents' },
        ...(isEnterprise ? [{ label: 'Enterprise', icon: Crown, path: '/portal/enterprise', premium: true }] : []),
      ]},
      { section: 'Wachstum', items: [
        { label: 'Boosts', icon: Sparkles, path: '/portal/boosts' },
        { label: 'Leads', icon: MessageSquarePlus, path: '/portal/leads' },
      ]},
      { section: 'Abrechnung', items: [
        { label: t('admin.nav.billing') || 'Abrechnung', icon: CreditCard, path: '/portal/billing' },
        { label: 'Bestellungen', icon: Receipt, path: '/portal/orders' },
        { label: 'Guthaben', icon: Wallet, path: '/portal/wallet' },
        { label: 'Empfehlungen', icon: Sparkles, path: '/portal/referrals' },
      ]},
      { section: 'Konto', items: [
        { label: 'Team', icon: Users, path: '/portal/team' },
        { label: 'Support', icon: MessageCircle, path: '/portal/support' },
        { label: t('settings.nav') || 'Integrationen', icon: Settings, path: '/portal/integrations' },
        { label: 'Sicherheit', icon: Shield, path: '/admin/security' },
        { label: t('profile.title') || 'Profil', icon: User, path: '/admin/profile' },
      ]},
    ],
  };
}

/* ─── Session timer chip ──────────────────────────────────────────────── */
function SessionTimer() {
  const { sessionRemaining } = useAuth();
  if (sessionRemaining == null) return null;
  const mins = Math.floor(sessionRemaining / 60000);
  const secs = Math.floor((sessionRemaining % 60000) / 1000);
  const isLow = sessionRemaining < 120000;
  return (
    <div
      className={`hidden lg:flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[10.5px] font-mono transition-colors ${
        isLow ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#F4F4F5] text-[#71717A] hover:bg-[#E4E4E7]'
      }`}
      data-testid="session-timer"
      title="Verbleibende Session-Zeit"
    >
      <Timer className="w-3 h-3" />
      <span className="tabular-nums">{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}</span>
    </div>
  );
}

/* ─── Sidebar nav with collapsible sections + filter ──────────────────── */
function SidebarNav({ menu, collapsed, location, onItemClick, query, collapsedSections, toggleSection }) {
  const q = (query || '').trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return menu;
    return menu
      .map(s => ({ ...s, items: s.items.filter(i => i.label.toLowerCase().includes(q)) }))
      .filter(s => s.items.length);
  }, [menu, q]);

  if (!filtered.length) {
    return (
      <div className="px-5 py-8 text-center">
        <p className="text-[12px] text-[#A1A1AA]">Keine Treffer</p>
      </div>
    );
  }

  return (
    <nav className="flex-1 overflow-y-auto py-3 px-1.5">
      {filtered.map((section, si) => {
        const collapsedSec = collapsedSections.has(section.section);
        return (
          <div key={section.section || si} className={si > 0 ? 'mt-1' : ''}>
            {!collapsed && (
              <button
                type="button"
                onClick={() => toggleSection(section.section)}
                className="group w-full flex items-center justify-between px-3.5 py-1.5 mt-2 mb-0.5 rounded-md hover:bg-[#F4F4F5] transition-colors"
                data-testid={`nav-section-${section.section.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="text-[9.5px] tracking-[0.22em] uppercase font-bold text-[#A1A1AA] group-hover:text-[#52525B] transition-colors">
                  {section.section}
                </span>
                <span className="text-[#D4D4D8] group-hover:text-[#71717A] transition-colors">
                  {collapsedSec ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                </span>
              </button>
            )}
            {collapsed && si > 0 && <div className="mx-3 my-2 h-px bg-[#E4E4E7]/60" aria-hidden />}
            <AnimatePresence initial={false}>
              {(!collapsedSec || collapsed) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease }}
                  className="overflow-hidden"
                >
                  {section.items.map((item) => (
                    <NavLinkItem
                      key={item.path}
                      item={item}
                      collapsed={collapsed}
                      isActive={location.pathname === item.path}
                      onClick={onItemClick}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </nav>
  );
}

function NavLinkItem({ item, collapsed, isActive, onClick }) {
  const Icon = item.icon;
  const isPremium = item.premium;
  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.path.split('/').pop()}`}
      className={`group relative flex items-center gap-3 px-3 py-2 mx-1 mb-0.5 rounded-lg text-[12.5px] transition-colors duration-150 ${
        isActive
          ? isPremium
            ? 'bg-gradient-to-r from-[#09090B] to-[#1A1F2E] text-white font-medium'
            : 'text-[#09090B] font-medium'
          : isPremium
            ? 'text-[#09090B] hover:bg-gradient-to-r hover:from-[#09090B] hover:to-[#1A1F2E] hover:text-white font-medium'
            : 'text-[#52525B] hover:text-[#09090B] hover:bg-[#F4F4F5]'
      }`}
      title={collapsed ? item.label : undefined}
    >
      {/* Animated active background pill (Linear-style) */}
      {isActive && !isPremium && (
        <motion.span
          layoutId="dash-active-pill"
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="absolute inset-0 rounded-lg bg-white shadow-[0_2px_8px_rgba(9,9,11,0.06),0_0_0_1px_rgba(9,9,11,0.04)]"
          aria-hidden
        />
      )}
      {/* Active accent dot — green pulse */}
      {isActive && !isPremium && !collapsed && (
        <motion.span
          layoutId="dash-active-dot"
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          className="absolute right-3 w-1.5 h-1.5 rounded-full bg-[#00D387]"
          style={{ boxShadow: '0 0 8px rgba(0,211,135,0.55)' }}
          aria-hidden
        />
      )}
      <Icon className={`relative w-[16px] h-[16px] flex-shrink-0 ${
        isActive
          ? isPremium ? 'text-white' : 'text-[#0F8A5F]'
          : isPremium ? 'text-[#CFEEFF]' : 'text-[#71717A] group-hover:text-[#52525B]'
      }`} />
      {!collapsed && (
        <span className="relative truncate flex-1 inline-flex items-center gap-1.5 tracking-tight">
          {item.label}
          {isPremium && (
            <span className={`text-[8.5px] tracking-[0.16em] uppercase font-black px-1.5 py-0.5 rounded ${
              isActive ? 'bg-white/15 text-[#CFEEFF]' : 'bg-[#09090B] text-[#CFEEFF]'
            }`}>ENT</span>
          )}
        </span>
      )}
    </Link>
  );
}

/* ─── Breadcrumb derived from path + menu lookup ──────────────────────── */
function Breadcrumb({ menu }) {
  const location = useLocation();
  const all = menu.flatMap(s => s.items);
  const current = all.find(i => i.path === location.pathname);
  // Determine root segment for "level 1"
  const seg1 = location.pathname.split('/').filter(Boolean)[0] || '';
  const root = seg1 === 'admin' ? 'Admin' : seg1 === 'portal' ? 'Portal' : seg1 === 'auditor' ? 'Auditor' : 'Zertivo';
  return (
    <div className="hidden sm:flex items-center gap-2 min-w-0">
      <span className="text-[11.5px] font-medium tracking-tight text-[#A1A1AA] flex-shrink-0">{root}</span>
      <span className="text-[#D4D4D8]">/</span>
      <span className="font-manrope text-[15px] sm:text-[16px] font-light tracking-tight text-[#09090B] truncate" data-testid="dash-breadcrumb-current">
        {current?.label || 'Übersicht'}
      </span>
    </div>
  );
}

/* ─── Top user dropdown — premium SaaS profile sheet ──────────────────── */
function UserMenu({ user, onLogout, lang, setLang, locales }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const roleLabel = user?.role === 'super_admin' ? 'Super-Admin' : user?.role === 'admin' ? 'Admin' : user?.role === 'auditor' ? 'Auditor' : user?.role === 'support' ? 'Support' : 'Company';
  const initial = (user?.name || user?.email || '?')[0].toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="group inline-flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-[#F4F4F5] transition-colors"
        data-testid="dash-user-menu-btn"
        aria-label="Mein Konto"
        aria-expanded={open}
      >
        <span className="relative w-7 h-7 rounded-full bg-gradient-to-br from-[#0F8A5F] to-[#00D387] flex items-center justify-center text-white text-[11.5px] font-bold ring-2 ring-white shadow-[0_2px_6px_rgba(15,138,95,0.25)]">
          {initial}
          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#00D387] ring-2 ring-white" aria-hidden />
        </span>
        <ChevronDown className={`w-3 h-3 text-[#71717A] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.99 }}
            transition={{ duration: 0.22, ease }}
            className="absolute right-0 top-12 z-40 w-[300px] rounded-2xl bg-white border border-[#E4E4E7]/60 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.18),0_8px_20px_-8px_rgba(0,0,0,0.08)] overflow-hidden"
            data-testid="dash-user-dropdown"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Hero banner — gradient with avatar */}
            <div className="relative h-20 bg-gradient-to-br from-[#054F35] via-[#0F8A5F] to-[#00D387] overflow-hidden">
              <div aria-hidden className="absolute inset-0 opacity-25" style={{
                backgroundImage: 'radial-gradient(circle at 80% 30%, rgba(255,255,255,0.4) 0%, transparent 50%)',
              }} />
              <div aria-hidden className="absolute -top-6 -right-4 w-32 h-32 rounded-full bg-white/15 blur-2xl" />
            </div>
            {/* Avatar overlapping banner */}
            <div className="relative px-5 -mt-7 pb-4">
              <div className="flex items-end justify-between mb-3">
                <span className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0F8A5F] to-[#00D387] flex items-center justify-center text-white text-[22px] font-light ring-4 ring-white shadow-[0_8px_24px_-8px_rgba(15,138,95,0.5)]">
                  {initial}
                </span>
                <span className="inline-flex items-center h-5 px-2 rounded-full bg-white border border-[#0F8A5F]/15 text-[#0F8A5F] text-[9.5px] font-bold tracking-wider uppercase mb-1">
                  {roleLabel}
                </span>
              </div>
              <p className="font-manrope text-[16px] font-light tracking-tight text-[#09090B] truncate">{user?.name || user?.email}</p>
              <p className="text-[11.5px] text-[#71717A] truncate font-light mt-0.5">{user?.email}</p>
            </div>

            {/* Menu items — sectioned */}
            <div className="px-2 pb-2 border-t border-[#F4F4F5]">
              <p className="text-[9.5px] tracking-[0.22em] uppercase font-bold text-[#A1A1AA] px-3 mt-2 mb-0.5">Konto</p>
              <Link to="/admin/profile" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#FAFAFA] text-[12.5px] text-[#09090B] transition-colors group">
                <span className="w-7 h-7 rounded-lg bg-[#F4F4F5] group-hover:bg-[#E6FBF3] flex items-center justify-center transition-colors">
                  <User className="w-3.5 h-3.5 text-[#52525B] group-hover:text-[#0F8A5F]" />
                </span>
                <span className="flex-1 font-medium">Mein Profil</span>
                <ArrowUpRight className="w-3 h-3 text-[#A1A1AA] opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <Link to="/admin/security" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#FAFAFA] text-[12.5px] text-[#09090B] transition-colors group">
                <span className="w-7 h-7 rounded-lg bg-[#F4F4F5] group-hover:bg-[#E6FBF3] flex items-center justify-center transition-colors">
                  <Shield className="w-3.5 h-3.5 text-[#52525B] group-hover:text-[#0F8A5F]" />
                </span>
                <span className="flex-1 font-medium">Sicherheit & 2FA</span>
                <ArrowUpRight className="w-3 h-3 text-[#A1A1AA] opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <a href="https://myzertivo.com" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#FAFAFA] text-[12.5px] text-[#09090B] transition-colors group">
                <span className="w-7 h-7 rounded-lg bg-[#F4F4F5] group-hover:bg-[#E6FBF3] flex items-center justify-center transition-colors">
                  <ArrowUpRight className="w-3.5 h-3.5 text-[#52525B] group-hover:text-[#0F8A5F]" />
                </span>
                <span className="flex-1 font-medium">Zur Website</span>
              </a>
            </div>

            {locales && locales.length > 1 && (
              <div className="px-3 py-3 border-t border-[#F4F4F5] flex items-center justify-between">
                <span className="text-[9.5px] tracking-[0.22em] uppercase font-bold text-[#71717A]">Sprache</span>
                <LangPicker lang={lang} setLang={setLang} locales={locales} />
              </div>
            )}

            <div className="p-2 border-t border-[#F4F4F5] bg-[#FAFAFA]/30">
              <button
                onClick={onLogout}
                data-testid="logout-btn"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#FEE2E2]/60 text-[12.5px] text-left transition-colors group"
              >
                <span className="w-7 h-7 rounded-lg bg-[#FEE2E2]/60 group-hover:bg-[#FEE2E2] flex items-center justify-center transition-colors">
                  <LogOut className="w-3.5 h-3.5 text-[#B91C1C]" />
                </span>
                <span className="flex-1 font-semibold text-[#B91C1C]">Abmelden</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Quick-search ⌘K modal ───────────────────────────────────────────── */
function QuickSearch({ open, onClose, menu }) {
  const [val, setVal] = useState('');
  const navigate = useNavigate();
  const all = useMemo(() => menu.flatMap(s => s.items.map(i => ({ ...i, sec: s.section }))), [menu]);
  const filtered = useMemo(() => {
    const q = val.trim().toLowerCase();
    if (!q) return all.slice(0, 8);
    return all.filter(i => i.label.toLowerCase().includes(q) || (i.sec || '').toLowerCase().includes(q)).slice(0, 10);
  }, [all, val]);

  useEffect(() => { if (open) setVal(''); }, [open]);

  const go = (path) => { onClose(); navigate(path); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[70] bg-[#09090B]/60 backdrop-blur-md flex items-start justify-center pt-24 px-5"
          onClick={onClose}
          data-testid="dash-search-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl rounded-2xl bg-white shadow-[0_24px_80px_-24px_rgba(0,0,0,0.25)] overflow-hidden border border-white/40"
          >
            <div className="flex items-center gap-3 px-5 h-14 border-b border-[#F4F4F5]">
              <Search className="w-4 h-4 text-[#71717A]" />
              <input
                autoFocus
                type="search"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder="Suche im Dashboard…"
                className="flex-1 bg-transparent border-0 outline-none text-[14px] text-[#09090B] placeholder:text-[#A1A1AA] tracking-tight"
                data-testid="dash-search-input"
              />
              <kbd className="px-1.5 py-0.5 rounded bg-[#F4F4F5] text-[10px] font-mono text-[#71717A]">ESC</kbd>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {filtered.length === 0 ? (
                <p className="px-4 py-8 text-center text-[12.5px] text-[#A1A1AA]">Nichts gefunden</p>
              ) : (
                filtered.map((i) => {
                  const Icon = i.icon;
                  return (
                    <button
                      type="button"
                      key={i.path}
                      onClick={() => go(i.path)}
                      data-testid={`dash-search-item-${i.path.split('/').pop()}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#FAFAFA] text-left group"
                    >
                      <span className="w-7 h-7 rounded-lg bg-[#F4F4F5] group-hover:bg-[#E6FBF3] flex items-center justify-center transition-colors">
                        <Icon className="w-3.5 h-3.5 text-[#52525B] group-hover:text-[#0F8A5F]" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <p className="text-[13px] text-[#09090B] tracking-tight truncate">{i.label}</p>
                        <p className="text-[10.5px] text-[#A1A1AA] uppercase tracking-wider">{i.sec}</p>
                      </span>
                      <ArrowUpRight className="w-3.5 h-3.5 text-[#A1A1AA] group-hover:text-[#0F8A5F] -translate-x-1 group-hover:translate-x-0 opacity-0 group-hover:opacity-100 transition-all" />
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MAIN LAYOUT COMPONENT
   ════════════════════════════════════════════════════════════════════════ */
export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { lang, setLang, locales } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('zv:dash:collapsed') === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [navQuery, setNavQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState(readCollapsedSections());

  const allMenus = useMenuItems();
  const menu = allMenus[user?.role] || allMenus.company;

  useEffect(() => {
    try { localStorage.setItem('zv:dash:collapsed', collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);
  useEffect(() => { setMobileOpen(false); setSearchOpen(false); }, [location]);

  // Cmd+K / Ctrl+K to open quick search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lock body scroll when mobile sidebar open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const toggleSection = (label) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      writeCollapsedSections(next);
      return next;
    });
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="flex h-screen overflow-hidden bg-[#FAFAFA]" data-testid="dashboard-layout">
      {/* Subtle dotted grid bg pattern — adds depth without distraction */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.4] [mask-image:radial-gradient(ellipse_at_top,_black_0%,_transparent_70%)]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(9,9,11,0.05) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#09090B]/40 backdrop-blur-sm md:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="dash-mobile-overlay"
          />
        )}
      </AnimatePresence>

      {/* ═══════════ SIDEBAR ═══════════ */}
      <aside
        className={`flex flex-col transition-all duration-300 z-50 fixed md:relative inset-y-0 left-0 bg-white/85 backdrop-blur-xl border-r border-[#E4E4E7]/60
          ${mobileOpen ? 'translate-x-0 shadow-[0_24px_60px_rgba(0,0,0,0.18)]' : '-translate-x-full md:translate-x-0'}
          ${collapsed ? 'w-[68px]' : 'w-[244px]'}`}
        data-testid="sidebar"
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-3.5 h-14 border-b border-[#E4E4E7]/60">
          <Link to="/" className={`group flex items-center ${collapsed ? 'justify-center w-full' : ''}`} data-testid="sidebar-logo">
            <span className="font-manrope text-[18px] font-extralight tracking-[-0.04em] text-[#09090B] inline-flex items-baseline">
              {collapsed ? 'Z' : 'Zertivo'}
              <span className="ml-0.5 relative inline-block w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-[#00D387]" />
                <span className="absolute inset-0 rounded-full bg-[#00D387] animate-ping opacity-40" />
              </span>
            </span>
          </Link>
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              className="hidden md:inline-flex w-7 h-7 rounded-md hover:bg-[#F4F4F5] text-[#A1A1AA] hover:text-[#52525B] items-center justify-center transition-colors"
              data-testid="toggle-sidebar-btn"
              title="Sidebar einklappen"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden w-7 h-7 rounded-md hover:bg-[#F4F4F5] text-[#71717A] flex items-center justify-center"
            aria-label="Menü schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Sidebar search (filter nav items) */}
        {!collapsed && (
          <div className="px-2.5 pt-2.5 pb-1">
            <div className="relative group">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A1A1AA] group-focus-within:text-[#0F8A5F] transition-colors" />
              <input
                type="text"
                value={navQuery}
                onChange={(e) => setNavQuery(e.target.value)}
                onFocus={() => setSearchOpen(false)}
                placeholder="Filtern…"
                className="w-full h-8 pl-8 pr-12 rounded-lg bg-[#F4F4F5]/60 border border-transparent focus:border-[#E4E4E7] focus:bg-white text-[12px] text-[#09090B] placeholder:text-[#A1A1AA] outline-none tracking-tight transition-all"
                data-testid="sidebar-filter-input"
              />
              <kbd className="absolute right-1.5 top-1/2 -translate-y-1/2 hidden lg:inline-flex items-center gap-0.5 h-5 px-1 rounded text-[9px] font-mono font-medium tracking-wider text-[#71717A] bg-white/80 border border-[#E4E4E7]">
                {isMac ? <Command className="w-2 h-2" /> : 'Ctrl'}K
              </kbd>
            </div>
          </div>
        )}

        {/* Collapsed expand button */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="hidden md:inline-flex mt-2 mx-auto w-7 h-7 rounded-md hover:bg-[#F4F4F5] text-[#A1A1AA] hover:text-[#52525B] items-center justify-center transition-colors"
            data-testid="toggle-sidebar-btn"
            title="Sidebar ausklappen"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}

        <SidebarNav
          menu={menu}
          collapsed={collapsed}
          location={location}
          onItemClick={() => setMobileOpen(false)}
          query={navQuery}
          collapsedSections={collapsedSections}
          toggleSection={toggleSection}
        />

        {/* Plan card — company role only */}
        {!collapsed && user?.role === 'company' && (
          <div className="px-3 pb-2">
            <PlanCard />
          </div>
        )}

        {/* Sidebar footer — minimal user pill (real menu is in topbar) */}
        <div className="border-t border-[#E4E4E7]/60 p-2.5">
          {!collapsed ? (
            <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0F8A5F] to-[#00D387] flex items-center justify-center text-white text-[11.5px] font-bold ring-2 ring-white shadow-sm flex-shrink-0">
                {(user?.name || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11.5px] font-medium tracking-tight text-[#09090B] truncate">{user?.name || user?.email}</p>
                <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-[#0F8A5F]">{user?.role === 'super_admin' ? 'Super-Admin' : user?.role === 'admin' ? 'Admin' : user?.role === 'auditor' ? 'Auditor' : user?.role === 'support' ? 'Support' : 'Company'}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0F8A5F] to-[#00D387] flex items-center justify-center text-white text-[11.5px] font-bold ring-2 ring-white shadow-sm">
                {(user?.name || '?')[0].toUpperCase()}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ═══════════ MAIN ═══════════ */}
      <main className="flex-1 overflow-y-auto min-w-0 relative">
        {/* Topbar — sticky, glass-morphic */}
        <div
          className="sticky top-0 z-30 flex items-center justify-between gap-3 px-4 md:px-6 lg:px-8 h-14 bg-[#FAFAFA]/80 backdrop-blur-xl border-b border-[#E4E4E7]/60"
          data-testid="dash-topbar"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-9 h-9 rounded-lg hover:bg-[#F4F4F5] text-[#52525B] flex items-center justify-center"
              data-testid="mobile-sidebar-btn"
              aria-label="Menü öffnen"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Breadcrumb menu={menu} />
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <SessionTimer />
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="inline-flex items-center gap-2 h-9 pl-3 pr-2 rounded-full bg-white border border-[#E4E4E7] hover:border-[#09090B]/30 hover:bg-[#FAFAFA] text-[#52525B] hover:text-[#09090B] transition-all group"
              data-testid="dash-search-toggle"
              aria-label="Schnellsuche (Strg+K)"
            >
              <Search className="w-3.5 h-3.5" />
              <span className="hidden md:inline text-[12px] font-light tracking-tight pr-1">Suchen</span>
              <span className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-[#F4F4F5] text-[9.5px] font-mono font-bold tracking-wider text-[#71717A] group-hover:bg-[#E4E4E7]">
                {isMac ? <Command className="w-2.5 h-2.5" /> : 'Ctrl'}K
              </span>
            </button>
            <NotificationBell />
            <UserMenu user={user} onLogout={handleLogout} lang={lang} setLang={setLang} locales={locales} />
          </div>
        </div>

        {/* Content area */}
        <div className="relative px-4 md:px-6 lg:px-8 py-5 md:py-6 lg:py-8 max-w-[1480px] mx-auto pb-24 md:pb-12">
          {children}
        </div>
      </main>

      <MobileBottomNav role={user?.role} onMoreClick={() => setMobileOpen(true)} />
      <QuickSearch open={searchOpen} onClose={() => setSearchOpen(false)} menu={menu} />
    </div>
  );
}
